<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { vMaska } from 'maska/vue'
import radiatorStandardImg from '../../assets/images/radiator_standard.png'
import radiatorDesignerImg from '../../assets/images/radiator_designer.png'
import { trackCalcOpen, trackCalcStart, trackCalcResult, trackCalcSubmit, trackLeadFormSubmit } from '../lib/goals.js'

// Настройки из API
const settings = ref(null)
const loading = ref(true)
const error = ref(null)

// Активный таб калькулятора
const activeTab = ref('floor') // floor, radiators, work

// Данные тёплого пола
const floorData = reactive({
  area: '',
  pipeType: 'evoh',
  pipeDiameter: 16
})

// Данные радиаторов
const radiatorData = reactive({
  rooms: [],
  currentRoom: {
    area: '',
    type: 'standard'
  },
  totalRadiators: 0,
  manualRadiators: false
})

// Данные стоимости работ
const workData = reactive({
  area: '',
  package: 'comfort'
})

// === ЗАЯВКА ===
const requestTab = ref('consultation') // dealer, consultation
const attachCalculation = ref(true)
const submitting = ref(false)
const submitMessage = ref(null) // { type: 'success'|'error', text: '' }

const dealerForm = reactive({
  company_name: '',
  city: '',
  name: '',
  phone: '',
  email: '',
  comment: ''
})

const consultationForm = reactive({
  service_interest: '',
  object_type: '',
  area: '',
  name: '',
  phone: '',
  comment: ''
})

const phoneMaskOptions = { mask: '+7 (###) ###-##-##', eager: true }

// Загрузка настроек
onMounted(async () => {
  try {
    const response = await fetch('/api/calculator_settings')
    settings.value = await response.json()
    loading.value = false
  } catch (e) {
    error.value = 'Ошибка загрузки настроек'
    loading.value = false
  }

  // YM: track when calculator section enters viewport
  const calcEl = document.getElementById('calculator')
  if (calcEl && 'IntersectionObserver' in window) {
    const obs = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        trackCalcOpen()
        obs.disconnect()
      }
    }, { threshold: 0.3 })
    obs.observe(calcEl)
  }
})

// === РАСЧЁТЫ ТЁПЛОГО ПОЛА ===
const floorResults = computed(() => {
  if (!settings.value || !floorData.area) return null

  const s = settings.value.floor_heating
  const area = parseFloat(floorData.area)
  if (isNaN(area) || area <= 0) return null

  const pipeLength = Math.ceil(area * s.pipe_coefficient)
  const mats = Math.ceil(area * s.mat_coefficient)
  const maxCircuit = floorData.pipeDiameter === 16 ? s.max_circuit_length_16mm : s.max_circuit_length_20mm
  const circuits = Math.ceil(pipeLength / maxCircuit)

  let collectorSize = 4
  if (circuits > 4 && circuits <= 6) collectorSize = 6
  else if (circuits > 6 && circuits <= 8) collectorSize = 8
  else if (circuits > 8) collectorSize = 12

  const euroconus = circuits * 2
  const pipeKey = `${floorData.pipeDiameter}_${floorData.pipeType}`
  const pipePrice = s.pipe_prices[pipeKey] || 0

  const pipeCost = pipeLength * pipePrice
  const matsCost = mats * s.mat_price_per_sqm
  const collectorCost = s.collector_prices[collectorSize] || 0
  const euroconusCost = euroconus * s.euroconus_price
  const materialsCost = pipeCost + matsCost + collectorCost + euroconusCost
  const workCost = area * s.work_price_per_sqm
  const totalCost = materialsCost + workCost

  return { area, pipeLength, mats, circuits, collectorSize, euroconus, pipeCost, matsCost, collectorCost, euroconusCost, materialsCost, workCost, totalCost }
})

// === РАСЧЁТЫ РАДИАТОРОВ ===
const addRoom = () => {
  if (!settings.value || !radiatorData.currentRoom.area) return
  const s = settings.value.radiators
  const area = parseFloat(radiatorData.currentRoom.area)
  if (isNaN(area) || area <= 0) return

  const coef = radiatorData.currentRoom.type === 'standard' ? s.coefficient_standard : s.coefficient_designer
  const sectionsBase = Math.ceil(area / coef) + 1
  const radiatorCount = Math.ceil(sectionsBase / s.max_sections_per_radiator)
  const sectionsPerRadiator = Math.ceil(sectionsBase / radiatorCount)

  radiatorData.rooms.push({ area, type: radiatorData.currentRoom.type, sections: sectionsBase, radiators: radiatorCount, sectionsPerRadiator })
  updateTotalRadiators()
  radiatorData.currentRoom.area = ''
}

const removeRoom = (index) => {
  radiatorData.rooms.splice(index, 1)
  updateTotalRadiators()
}

const updateTotalRadiators = () => {
  if (!radiatorData.manualRadiators) {
    radiatorData.totalRadiators = radiatorData.rooms.reduce((sum, room) => sum + room.radiators, 0)
  }
}

const radiatorResults = computed(() => {
  if (!settings.value || radiatorData.rooms.length === 0) return null
  const s = settings.value.radiators

  const totalSections = radiatorData.rooms.reduce((sum, room) => sum + room.sections, 0)
  const totalRadiators = radiatorData.manualRadiators
    ? radiatorData.totalRadiators
    : radiatorData.rooms.reduce((sum, room) => sum + room.radiators, 0)

  const standardSections = radiatorData.rooms.filter(r => r.type === 'standard').reduce((sum, r) => sum + r.sections, 0)
  const designerSections = radiatorData.rooms.filter(r => r.type === 'designer').reduce((sum, r) => sum + r.sections, 0)

  const sectionsCost = (standardSections * s.section_price_standard) + (designerSections * s.section_price_designer)
  const mountingKitsCost = totalRadiators * s.mounting_kit_price
  const valveKitsCost = totalRadiators * s.valve_kit_price
  const totalCost = sectionsCost + mountingKitsCost + valveKitsCost

  return { totalSections, totalRadiators, standardSections, designerSections, sectionsCost, mountingKitsCost, valveKitsCost, totalCost }
})

// === РАСЧЁТЫ СТОИМОСТИ РАБОТ ===
const workResults = computed(() => {
  if (!settings.value || !workData.area) return null
  const s = settings.value.work_packages
  const area = parseFloat(workData.area)
  if (isNaN(area) || area <= 0) return null

  const pricePerSqm = s[workData.package] || 0
  const totalCost = area * pricePerSqm
  return { area, package: workData.package, pricePerSqm, totalCost }
})

// Есть ли хоть один расчёт
const hasCalculation = computed(() => !!(floorResults.value || radiatorResults.value || workResults.value))

// Собрать данные калькулятора для заявки
const getCalculatorData = () => {
  if (!attachCalculation.value || !hasCalculation.value) return null

  const data = {}
  if (floorResults.value) {
    data.floor = {
      area: floorResults.value.area,
      pipeLength: floorResults.value.pipeLength,
      circuits: floorResults.value.circuits,
      totalCost: floorResults.value.totalCost
    }
  }
  if (radiatorResults.value) {
    data.radiators = {
      rooms: radiatorData.rooms.map(room => ({
        area: room.area,
        type: room.type,
        sections: room.sections,
        radiators: room.radiators,
        sectionsPerRadiator: room.sectionsPerRadiator
      })),
      totalSections: radiatorResults.value.totalSections,
      totalRadiators: radiatorResults.value.totalRadiators,
      totalCost: radiatorResults.value.totalCost
    }
  }
  if (workResults.value) {
    data.work = {
      area: workResults.value.area,
      package: workResults.value.package,
      totalCost: workResults.value.totalCost
    }
  }
  return data
}

// Краткое описание прикреплённого расчёта
const calculationSummary = computed(() => {
  const parts = []
  if (floorResults.value) parts.push(`Тёплый пол ${floorResults.value.area} м² — ${formatPrice(floorResults.value.totalCost)} ₸`)
  if (radiatorResults.value) parts.push(`Радиаторы ${radiatorResults.value.totalSections} секций — ${formatPrice(radiatorResults.value.totalCost)} ₸`)
  if (workResults.value) parts.push(`Работы ${workResults.value.area} м² — ${formatPrice(workResults.value.totalCost)} ₸`)
  return parts
})

// Track calculator usage in GA4 (once per tab per session, diagnostic micro-conversion)
const firedCalcEvents = new Set()
const trackCalculatorComplete = (calcType) => {
  trackCalcResult()
  if (firedCalcEvents.has(calcType)) return
  if (typeof window.gtag !== 'function') return
  window.gtag('event', 'calculator_complete', { calc_type: calcType })
  firedCalcEvents.add(calcType)
}

watch(floorResults, (val) => { if (val) trackCalculatorComplete('floor') })
watch(radiatorResults, (val) => { if (val) trackCalculatorComplete('radiators') })
watch(workResults, (val) => { if (val) trackCalculatorComplete('work') })

// YM: track first user input in any calculator tab
watch(
  () => [floorData.area, radiatorData.currentRoom.area, workData.area],
  (vals) => { if (vals.some(v => v && parseFloat(v) > 0)) trackCalcStart() }
)

// Отправка заявки
const isPhoneComplete = (phone) => phone.replace(/\D/g, '').length === 11

const submitRequest = async () => {
  const isDealer = requestTab.value === 'dealer'
  const form = isDealer ? dealerForm : consultationForm

  if (!isPhoneComplete(form.phone)) {
    submitMessage.value = { type: 'error', text: 'Введите полный номер телефона' }
    return
  }

  const data = {
    request_type: isDealer ? 'dealer' : 'consultation',
    name: form.name,
    phone: form.phone,
    comment: form.comment || undefined
  }

  if (isDealer) {
    data.company_name = form.company_name
    data.city = form.city
    data.email = form.email || undefined
  } else {
    data.service_interest = form.service_interest
    data.object_type = form.object_type || undefined
    data.area = form.area || undefined
  }

  const calcData = getCalculatorData()
  if (calcData) data.calculator_data = calcData

  submitting.value = true
  submitMessage.value = null

  try {
    const response = await fetch('/api/requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    if (response.ok) {
      // Track lead generation in GA4 (primary conversion for Google Ads)
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          value: 1,
          currency: 'KZT',
          lead_type: isDealer ? 'dealer' : 'consultation',
          source: 'landing_form'
        })
      }

      // Yandex.Metrika goals
      trackLeadFormSubmit({ lead_type: isDealer ? 'dealer' : 'consultation' })
      if (calcData) {
        trackCalcSubmit({ lead_type: isDealer ? 'dealer' : 'consultation' })
      }

      // Очистить форму
      if (isDealer) {
        Object.assign(dealerForm, { company_name: '', city: '', name: '', phone: '', email: '', comment: '' })
      } else {
        Object.assign(consultationForm, { service_interest: '', object_type: '', area: '', name: '', phone: '', comment: '' })
      }
      submitMessage.value = { type: 'success', text: 'Заявка отправлена! Мы свяжемся с вами в ближайшее время.' }
      setTimeout(() => { submitMessage.value = null }, 5000)
    } else {
      const result = await response.json()
      const errors = result.errors || {}
      const fieldNames = {
        name: 'Имя', phone: 'Телефон', company_name: 'Компания',
        city: 'Город', service_interest: 'Направление', email: 'Email'
      }
      const messages = Object.entries(errors).map(([field, msgs]) =>
        `${fieldNames[field] || field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
      )
      submitMessage.value = { type: 'error', text: messages.join('. ') || 'Ошибка при отправке' }
    }
  } catch {
    submitMessage.value = { type: 'error', text: 'Ошибка сети. Попробуйте позже или позвоните нам.' }
  } finally {
    submitting.value = false
  }
}

const formatPrice = (value) => {
  if (!value) return '0'
  return new Intl.NumberFormat('ru-RU').format(Math.round(value))
}

const packageNames = { luxury: 'LUXURY', comfort: 'КОМФОРТ', economy: 'ЭКОНОМ' }

const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition'
const selectClass = inputClass + ' bg-white'
</script>

<template>
  <div>
    <!-- ==================== КАЛЬКУЛЯТОР ==================== -->
    <section class="py-16 md:py-24 bg-gray-light" id="calculator">
      <div class="container mx-auto px-5 max-w-[900px]">
        <div class="text-center mb-10">
          <h2 class="text-2xl md:text-4xl font-bold text-dark mb-4">Калькулятор</h2>
          <p class="text-gray-500">Рассчитайте стоимость материалов и работ</p>
        </div>

        <!-- Загрузка -->
        <div v-if="loading" class="text-center py-12">
          <div class="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p class="mt-4 text-gray-500">Загрузка калькулятора...</p>
        </div>

        <!-- Ошибка -->
        <div v-else-if="error" class="text-center py-12 text-red-500">{{ error }}</div>

        <!-- Калькулятор -->
        <div v-else>
          <!-- Табы -->
          <div class="flex flex-wrap gap-2 mb-8">
            <button v-for="tab in [{id:'floor',label:'🔥 Тёплый пол'},{id:'radiators',label:'🌡️ Радиаторы'},{id:'work',label:'💼 Стоимость работ'}]" :key="tab.id"
              @click="activeTab = tab.id"
              :class="['px-6 py-3 rounded-lg font-semibold transition-all', activeTab === tab.id ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50']"
            >{{ tab.label }}</button>
          </div>

          <!-- ТЁПЛЫЙ ПОЛ -->
          <div v-if="activeTab === 'floor'" class="space-y-6">
            <div class="bg-white rounded-xl p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-6">Расчёт системы тёплого пола</h3>
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Площадь помещения (м²)</label>
                <input v-model="floorData.area" type="number" min="1" placeholder="Введите площадь" :class="inputClass">
              </div>
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Тип трубы</label>
                <div class="grid grid-cols-2 gap-3">
                  <button v-for="opt in [{id:'evoh',title:'С кислородным барьером',sub:'EVOH — рекомендуется'},{id:'no_barrier',title:'Без барьера',sub:'Бюджетный вариант'}]" :key="opt.id"
                    @click="floorData.pipeType = opt.id"
                    :class="['p-4 rounded-lg border-2 text-left transition-all', floorData.pipeType === opt.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300']"
                  >
                    <div class="font-semibold">{{ opt.title }}</div>
                    <div class="text-sm text-gray-500">{{ opt.sub }}</div>
                  </button>
                </div>
              </div>
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Диаметр трубы</label>
                <div class="grid grid-cols-2 gap-3">
                  <button v-for="opt in [{id:16,title:'16 мм',sub:'Стандартный'},{id:20,title:'20 мм',sub:'Для больших площадей'}]" :key="opt.id"
                    @click="floorData.pipeDiameter = opt.id"
                    :class="['p-4 rounded-lg border-2 text-left transition-all', floorData.pipeDiameter === opt.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300']"
                  >
                    <div class="font-semibold">{{ opt.title }}</div>
                    <div class="text-sm text-gray-500">{{ opt.sub }}</div>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="floorResults" class="bg-white rounded-xl p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-6">Результат расчёта</h3>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div class="bg-gray-50 rounded-lg p-4"><div class="text-2xl font-bold text-primary">{{ floorResults.pipeLength }} м</div><div class="text-sm text-gray-500">Труба</div></div>
                <div class="bg-gray-50 rounded-lg p-4"><div class="text-2xl font-bold text-primary">{{ floorResults.mats }} шт</div><div class="text-sm text-gray-500">Маты</div></div>
                <div class="bg-gray-50 rounded-lg p-4"><div class="text-2xl font-bold text-primary">{{ floorResults.circuits }}</div><div class="text-sm text-gray-500">Контуров</div></div>
                <div class="bg-gray-50 rounded-lg p-4"><div class="text-2xl font-bold text-primary">{{ floorResults.collectorSize }}</div><div class="text-sm text-gray-500">Коллектор (выходов)</div></div>
              </div>
              <div class="border-t pt-6 space-y-3">
                <div class="flex justify-between"><span class="text-gray-600">Труба ({{ floorResults.pipeLength }} м)</span><span class="font-medium">{{ formatPrice(floorResults.pipeCost) }} ₸</span></div>
                <div class="flex justify-between"><span class="text-gray-600">Маты ({{ floorResults.mats }} шт)</span><span class="font-medium">{{ formatPrice(floorResults.matsCost) }} ₸</span></div>
                <div class="flex justify-between"><span class="text-gray-600">Коллектор на {{ floorResults.collectorSize }} выходов</span><span class="font-medium">{{ formatPrice(floorResults.collectorCost) }} ₸</span></div>
                <div class="flex justify-between"><span class="text-gray-600">Евроконусы ({{ floorResults.euroconus }} шт)</span><span class="font-medium">{{ formatPrice(floorResults.euroconusCost) }} ₸</span></div>
                <div class="flex justify-between border-t pt-3"><span class="text-gray-800 font-semibold">Материалы</span><span class="font-bold text-primary">{{ formatPrice(floorResults.materialsCost) }} ₸</span></div>
                <div class="flex justify-between"><span class="text-gray-600">Работа ({{ floorResults.area }} м²)</span><span class="font-medium">{{ formatPrice(floorResults.workCost) }} ₸</span></div>
                <div class="flex justify-between border-t pt-3 text-lg"><span class="text-gray-800 font-bold">ИТОГО</span><span class="font-bold text-primary">{{ formatPrice(floorResults.totalCost) }} ₸</span></div>
              </div>
              <p class="text-sm text-gray-400 mt-4">* Расчёт ориентировочный. Точная стоимость после осмотра объекта.</p>
            </div>
          </div>

          <!-- РАДИАТОРЫ -->
          <div v-if="activeTab === 'radiators'" class="space-y-6">
            <div class="bg-white rounded-xl p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-6">Добавить комнату</h3>
              <div class="mb-5">
                <label class="block text-sm font-medium text-gray-700 mb-3">Тип радиатора</label>
                <div class="grid grid-cols-2 gap-4">
                  <div @click="radiatorData.currentRoom.type = 'standard'"
                    :class="['relative cursor-pointer rounded-xl border-2 overflow-hidden transition h-40', radiatorData.currentRoom.type === 'standard' ? 'border-primary ring-4 ring-primary/40 scale-[1.02]' : 'border-transparent opacity-60 hover:opacity-80']"
                    style="background-color: #2d2d2d">
                    <img :src="radiatorStandardImg" alt="Обычный" class="absolute inset-0 w-full h-full object-contain">
                    <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div class="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <p class="text-sm font-semibold">Обычный</p>
                      <p class="text-xs text-white/70">Revolution B 500 &middot; 564&times;80&times;80 мм</p>
                    </div>
                  </div>
                  <div @click="radiatorData.currentRoom.type = 'designer'"
                    :class="['relative cursor-pointer rounded-xl border-2 overflow-hidden transition h-40', radiatorData.currentRoom.type === 'designer' ? 'border-primary ring-4 ring-primary/40 scale-[1.02]' : 'border-transparent opacity-60 hover:opacity-80']"
                    style="background-color: #2d2d2d">
                    <img :src="radiatorDesignerImg" alt="Дизайнерский" class="absolute inset-0 w-full h-full object-contain">
                    <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div class="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <p class="text-sm font-semibold">Дизайнерский</p>
                      <p class="text-xs text-white/70">Радиатор стальной &middot; 500&times;44&times;70 мм</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Площадь комнаты (м²)</label>
                  <input v-model="radiatorData.currentRoom.area" type="number" min="1" placeholder="Площадь" :class="inputClass">
                </div>
                <div class="flex items-end">
                  <button @click="addRoom" :disabled="!radiatorData.currentRoom.area" class="w-full px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-hover transition disabled:opacity-50 disabled:cursor-not-allowed">Добавить комнату</button>
                </div>
              </div>
            </div>

            <div v-if="radiatorData.rooms.length > 0" class="bg-white rounded-xl p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-4">Добавленные комнаты</h3>
              <div class="space-y-3">
                <div v-for="(room, index) in radiatorData.rooms" :key="index" class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span class="font-medium">Комната {{ index + 1 }}</span>
                    <span class="text-gray-500 ml-2">{{ room.area }} м²</span>
                    <span class="text-gray-400 ml-2">&bull;</span>
                    <span class="text-gray-500 ml-2">{{ room.type === 'standard' ? 'Обычный' : 'Дизайнерский' }}</span>
                    <span class="text-gray-400 ml-2">&bull;</span>
                    <span class="text-primary ml-2">{{ room.sections }} секций</span>
                    <span v-if="room.radiators > 1" class="text-orange-500 ml-2">({{ room.radiators }} радиатора)</span>
                  </div>
                  <button @click="removeRoom(index)" class="text-red-500 hover:text-red-700">
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>
              <div class="mt-6 p-4 bg-blue-50 rounded-lg">
                <label class="flex items-center gap-2 mb-3">
                  <input type="checkbox" v-model="radiatorData.manualRadiators" class="w-4 h-4 text-primary border-gray-300 rounded">
                  <span class="text-sm text-gray-700">Указать количество радиаторов вручную</span>
                </label>
                <input v-if="radiatorData.manualRadiators" v-model.number="radiatorData.totalRadiators" type="number" min="1" class="w-32 px-3 py-2 border border-gray-300 rounded-lg">
              </div>
            </div>

            <div v-if="radiatorResults" class="bg-white rounded-xl p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-6">Результат расчёта</h3>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div class="bg-gray-50 rounded-lg p-4"><div class="text-2xl font-bold text-primary">{{ radiatorResults.totalSections }}</div><div class="text-sm text-gray-500">Всего секций</div></div>
                <div class="bg-gray-50 rounded-lg p-4"><div class="text-2xl font-bold text-primary">{{ radiatorResults.totalRadiators }}</div><div class="text-sm text-gray-500">Радиаторов</div></div>
                <div class="bg-gray-50 rounded-lg p-4"><div class="text-2xl font-bold text-primary">{{ radiatorData.rooms.length }}</div><div class="text-sm text-gray-500">Комнат</div></div>
              </div>
              <div class="border-t pt-6 space-y-3">
                <div class="flex justify-between"><span class="text-gray-600">Секции радиаторов</span><span class="font-medium">{{ formatPrice(radiatorResults.sectionsCost) }} ₸</span></div>
                <div class="flex justify-between"><span class="text-gray-600">Монтажные комплекты ({{ radiatorResults.totalRadiators }} шт)</span><span class="font-medium">{{ formatPrice(radiatorResults.mountingKitsCost) }} ₸</span></div>
                <div class="flex justify-between"><span class="text-gray-600">Комплекты кранов ({{ radiatorResults.totalRadiators }} шт)</span><span class="font-medium">{{ formatPrice(radiatorResults.valveKitsCost) }} ₸</span></div>
                <div class="flex justify-between border-t pt-3 text-lg"><span class="text-gray-800 font-bold">ИТОГО</span><span class="font-bold text-primary">{{ formatPrice(radiatorResults.totalCost) }} ₸</span></div>
              </div>
              <p class="text-sm text-gray-400 mt-4">* Расчёт ориентировочный. Точная стоимость после осмотра объекта.</p>
            </div>
          </div>

          <!-- СТОИМОСТЬ РАБОТ -->
          <div v-if="activeTab === 'work'" class="space-y-6">
            <div class="bg-white rounded-xl p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-2">Расчёт стоимости работ</h3>
              <p class="text-gray-500 text-sm mb-6">Цены указаны только за работу, без учёта материалов</p>
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2">Площадь квартиры/дома (м²)</label>
                <input v-model="workData.area" type="number" min="1" placeholder="Введите площадь" :class="inputClass">
              </div>
              <div class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-3">Выберите пакет</label>
                <div class="grid md:grid-cols-3 gap-4">
                  <button @click="workData.package = 'luxury'" :class="['p-5 rounded-xl border-2 text-left transition-all', workData.package === 'luxury' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300']">
                    <div class="text-2xl mb-2">🖤</div>
                    <div class="font-bold text-lg">LUXURY</div>
                    <div class="text-primary font-semibold">от {{ formatPrice(settings.work_packages.luxury) }} ₸/м²</div>
                    <ul class="mt-3 text-sm text-gray-500 space-y-1"><li>• Встраиваемые смесители</li><li>• Дизайнерские радиаторы</li><li>• Аксиальная разводка</li><li>• Скрытые узлы ввода</li></ul>
                  </button>
                  <button @click="workData.package = 'comfort'" :class="['p-5 rounded-xl border-2 text-left transition-all relative', workData.package === 'comfort' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300']">
                    <div class="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">Популярный</div>
                    <div class="text-2xl mb-2">🔘</div>
                    <div class="font-bold text-lg">КОМФОРТ</div>
                    <div class="text-primary font-semibold">от {{ formatPrice(settings.work_packages.comfort) }} ₸/м²</div>
                    <ul class="mt-3 text-sm text-gray-500 space-y-1"><li>• Накладные смесители</li><li>• Стандартные радиаторы</li><li>• Качественная разводка</li><li>• Инсталляция унитаза</li></ul>
                  </button>
                  <button @click="workData.package = 'economy'" :class="['p-5 rounded-xl border-2 text-left transition-all', workData.package === 'economy' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300']">
                    <div class="text-2xl mb-2">🔧</div>
                    <div class="font-bold text-lg">БЮДЖЕТ</div>
                    <div class="text-primary font-semibold">от {{ formatPrice(settings.work_packages.economy) }} ₸/м²</div>
                    <ul class="mt-3 text-sm text-gray-500 space-y-1"><li>• Простая сантехника</li><li>• Обычный унитаз</li><li>• Полипропилен</li><li>• Базовый монтаж</li></ul>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="workResults" class="bg-white rounded-xl p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-800 mb-6">Результат расчёта</h3>
              <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
                <div><span class="text-gray-600">Пакет</span><span class="font-bold text-lg ml-2">{{ packageNames[workResults.package] }}</span></div>
                <div><span class="text-gray-600">Площадь</span><span class="font-bold text-lg ml-2">{{ workResults.area }} м²</span></div>
              </div>
              <div class="border-t pt-6">
                <div class="flex justify-between items-center">
                  <div>
                    <div class="text-gray-600">{{ workResults.area }} м² × {{ formatPrice(workResults.pricePerSqm) }} ₸</div>
                    <div class="text-sm text-gray-400 mt-1">Стоимость только за работу</div>
                  </div>
                  <div class="text-3xl font-bold text-primary">{{ formatPrice(workResults.totalCost) }} ₸</div>
                </div>
              </div>
              <p class="text-sm text-gray-400 mt-6">* Расчёт ориентировочный. Материалы рассчитываются отдельно. Точная стоимость после осмотра объекта.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== ЗАЯВКА ==================== -->
    <section class="py-16 md:py-24 bg-dark" id="request">
      <div class="container mx-auto px-5 max-w-[900px]">
        <div class="text-center mb-10">
          <h2 class="text-2xl md:text-4xl font-bold text-white mb-4">Оставить заявку</h2>
          <p class="text-white/70">Выберите тип обращения</p>
        </div>

        <!-- Табы -->
        <div class="flex flex-col sm:flex-row gap-4 mb-8">
          <button @click="requestTab = 'dealer'"
            :class="['flex-1 py-4 px-6 rounded-lg font-semibold transition-all', requestTab === 'dealer' ? 'bg-white text-primary' : 'bg-white/10 text-white hover:bg-white/20']">
            <span class="block text-lg mb-1">Стать партнёром</span>
            <span class="block text-sm opacity-70 font-normal">Дилерам и дистрибьюторам</span>
          </button>
          <button @click="requestTab = 'consultation'"
            :class="['flex-1 py-4 px-6 rounded-lg font-semibold transition-all', requestTab === 'consultation' ? 'bg-white text-primary' : 'bg-white/10 text-white hover:bg-white/20']">
            <span class="block text-lg mb-1">Получить консультацию</span>
            <span class="block text-sm opacity-70 font-normal">Вопросы по товарам и услугам</span>
          </button>
        </div>

        <!-- Форма дилера -->
        <div v-if="requestTab === 'dealer'" class="bg-white rounded-lg p-6 md:p-10">
          <h3 class="text-xl font-bold text-dark mb-6">Заявка на партнёрство</h3>
          <form @submit.prevent="submitRequest" class="space-y-5">
            <div class="grid md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Название компании *</label>
                <input v-model="dealerForm.company_name" type="text" required :class="inputClass" placeholder="ТОО «Компания»">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Город *</label>
                <input v-model="dealerForm.city" type="text" required :class="inputClass" placeholder="Алматы">
              </div>
            </div>
            <div class="grid md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Контактное лицо *</label>
                <input v-model="dealerForm.name" type="text" required :class="inputClass" placeholder="Иван Иванов">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Телефон *</label>
                <input v-model="dealerForm.phone" v-maska="phoneMaskOptions" type="tel" required :class="inputClass" placeholder="+7 (___) ___-__-__">
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input v-model="dealerForm.email" type="email" :class="inputClass" placeholder="email@company.kz">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Комментарий</label>
              <textarea v-model="dealerForm.comment" rows="3" :class="inputClass + ' resize-none'" placeholder="Расскажите о вашей компании и интересующих направлениях"></textarea>
            </div>

            <!-- Прикрепить расчёт -->
            <div v-if="hasCalculation" class="p-4 bg-blue-50 rounded-lg">
              <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" v-model="attachCalculation" class="w-4 h-4 mt-0.5 text-primary border-gray-300 rounded">
                <div>
                  <span class="text-sm font-medium text-gray-700">Прикрепить расчёт из калькулятора</span>
                  <div class="mt-1 space-y-0.5">
                    <p v-for="line in calculationSummary" :key="line" class="text-xs text-gray-500">{{ line }}</p>
                  </div>
                </div>
              </label>
            </div>

            <!-- Сообщение -->
            <div v-if="submitMessage" :class="['p-4 rounded-lg text-sm font-medium', submitMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200']">
              {{ submitMessage.text }}
            </div>

            <button type="submit" :disabled="submitting" class="w-full py-4 bg-primary text-white font-semibold uppercase tracking-wide rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50">
              {{ submitting ? 'Отправка...' : 'Отправить заявку' }}
            </button>
          </form>
        </div>

        <!-- Форма консультации -->
        <div v-if="requestTab === 'consultation'" class="bg-white rounded-lg p-6 md:p-10">
          <h3 class="text-xl font-bold text-dark mb-6">Получить консультацию</h3>
          <form @submit.prevent="submitRequest" class="space-y-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Что вас интересует? *</label>
              <select v-model="consultationForm.service_interest" required :class="selectClass">
                <option value="">Выберите направление</option>
                <option value="materials">Поставка материалов (опт)</option>
                <option value="heating">Монтаж системы отопления</option>
                <option value="boiler">Котельное оборудование</option>
                <option value="sewage">Канализация</option>
                <option value="bio">Станции биоочистки</option>
                <option value="complex">Комплекс работ под ключ</option>
              </select>
            </div>
            <div class="grid md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Тип объекта</label>
                <select v-model="consultationForm.object_type" :class="selectClass">
                  <option value="">Выберите тип</option>
                  <option value="house">Частный дом / Коттедж</option>
                  <option value="apartment">Квартира</option>
                  <option value="office">Офис / Магазин</option>
                  <option value="restaurant">Ресторан / Кафе</option>
                  <option value="warehouse">Склад / Производство</option>
                  <option value="other">Другое</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Площадь объекта (м²)</label>
                <input v-model="consultationForm.area" type="text" :class="inputClass" placeholder="Примерная площадь">
              </div>
            </div>
            <div class="grid md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Ваше имя *</label>
                <input v-model="consultationForm.name" type="text" required :class="inputClass" placeholder="Как к вам обращаться">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Телефон *</label>
                <input v-model="consultationForm.phone" v-maska="phoneMaskOptions" type="tel" required :class="inputClass" placeholder="+7 (___) ___-__-__">
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Опишите задачу</label>
              <textarea v-model="consultationForm.comment" rows="3" :class="inputClass + ' resize-none'" placeholder="Укажите детали проекта: что нужно сделать, сроки, особые требования"></textarea>
            </div>

            <!-- Прикрепить расчёт -->
            <div v-if="hasCalculation" class="p-4 bg-blue-50 rounded-lg">
              <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" v-model="attachCalculation" class="w-4 h-4 mt-0.5 text-primary border-gray-300 rounded">
                <div>
                  <span class="text-sm font-medium text-gray-700">Прикрепить расчёт из калькулятора</span>
                  <div class="mt-1 space-y-0.5">
                    <p v-for="line in calculationSummary" :key="line" class="text-xs text-gray-500">{{ line }}</p>
                  </div>
                </div>
              </label>
            </div>

            <!-- Сообщение -->
            <div v-if="submitMessage" :class="['p-4 rounded-lg text-sm font-medium', submitMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200']">
              {{ submitMessage.text }}
            </div>

            <button type="submit" :disabled="submitting" class="w-full py-4 bg-primary text-white font-semibold uppercase tracking-wide rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50">
              {{ submitting ? 'Отправка...' : 'Получить консультацию' }}
            </button>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>
